import { groupApi as api } from './groupApi';
export const addTagTypes = ['Groups'] as const;
const injectedRtkApi = api
  .enhanceEndpoints({
    addTagTypes
  })
  .injectEndpoints({
    endpoints: build => ({
      getGroup: build.query<GetGroupApiResponse, GetGroupApiArg>({
        query: queryArg => ({
          url: `/group`,
          params: {
            search: queryArg.search,
            offset: queryArg.offset,
            limit: queryArg.limit,
            sortby: queryArg.sortby,
            orderby: queryArg.orderby,
            userId: queryArg.userId,
            isMyFiles: queryArg.isMyFiles,
            contributor: queryArg.contributor,
            ownerId: queryArg.ownerId
          }
        }),
        providesTags: ['Groups']
      }),
      addGroup: build.mutation<AddGroupApiResponse, AddGroupApiArg>({
        query: queryArg => ({
          url: `/group`,
          method: 'POST',
          body: queryArg.createGroup
        }),
        invalidatesTags: ['Groups']
      }),
      updateGroupMembers: build.mutation<
        UpdateGroupMembersApiResponse,
        UpdateGroupMembersApiArg
      >({
        query: queryArg => ({
          url: `/group/${queryArg.id}`,
          method: 'PUT',
          body: queryArg.updateGroup
        }),
        invalidatesTags: ['Groups']
      }),
      deleteGroup: build.mutation<DeleteGroupApiResponse, DeleteGroupApiArg>({
        query: queryArg => ({
          url: `/group/${queryArg.id}`,
          method: 'DELETE',
          params: { delete: queryArg['delete'] }
        }),
        invalidatesTags: ['Groups']
      }),
      getGroupMembersById: build.query<
        GetGroupMembersByIdApiResponse,
        GetGroupMembersByIdApiArg
      >({
        query: queryArg => ({ url: `/groupMembers/${queryArg.id}` }),
        providesTags: ['Groups']
      }),
      getGroupPeople: build.query<
        GetGroupPeopleApiResponse,
        GetGroupPeopleApiArg
      >({
        query: queryArg => ({
          url: `/groupPeople`,
          params: { id: queryArg.id, search: queryArg.search }
        }),
        providesTags: ['Groups']
      }),
      deleteGroupAsset: build.mutation<
        DeleteGroupAssetApiResponse,
        DeleteGroupAssetApiArg
      >({
        query: queryArg => ({
          url: `/group/assetDelete`,
          method: 'DELETE',
          body: queryArg.groupDelete
        }),
        invalidatesTags: ['Groups']
      }),
      removeGroupMember: build.mutation<
        RemoveGroupMemberApiResponse,
        RemoveGroupMemberApiArg
      >({
        query: queryArg => ({
          url: `/group/${queryArg.id}/member/${queryArg.peopleId}`,
          method: 'DELETE'
        }),
        invalidatesTags: ['Groups']
      }),
      mergeGroupAssets: build.mutation<
        MergeGroupAssetsApiResponse,
        MergeGroupAssetsApiArg
      >({
        query: queryArg => ({
          url: `/group/assets/merge`,
          method: 'POST',
          body: queryArg.body
        }),
        invalidatesTags: ['Groups']
      }),
      addGroupAssets: build.mutation<
        AddGroupAssetsApiResponse,
        AddGroupAssetsApiArg
      >({
        query: queryArg => ({
          url: `/group/addAssets`,
          method: 'POST',
          body: queryArg.groupAssets
        }),
        invalidatesTags: ['Groups']
      }),
      getGroupPermissions: build.query<
        GetGroupPermissionsApiResponse,
        GetGroupPermissionsApiArg
      >({
        query: queryArg => ({
          url: `/groupPermissions`,
          params: { groupId: queryArg.groupId }
        }),
        providesTags: ['Groups']
      }),
      updateGroupName: build.mutation<
        UpdateGroupNameApiResponse,
        UpdateGroupNameApiArg
      >({
        query: queryArg => ({
          url: `/groupName/${queryArg.id}`,
          method: 'PUT',
          body: queryArg.updateGroupName
        }),
        invalidatesTags: ['Groups']
      })
    }),
    overrideExisting: false
  });
export { injectedRtkApi as enhancedApi };
export type GetGroupApiResponse = /** status 200 Groups found. */ {
  ok?: boolean;
  data?: Group;
  message?: string;
};
export type GetGroupApiArg = {
  /** Search text */
  search?: string;
  /** offset for pagination */
  offset?: number;
  /** number of rows to fetch */
  limit?: number;
  /** sort */
  sortby?: string;
  /** order */
  orderby?: string;
  /** userId */
  userId?: string;
  /** Flag to filter users own files */
  isMyFiles?: boolean;
  /** Contributor ID */
  contributor?: string;
  /** Owner ID */
  ownerId?: string;
};
export type AddGroupApiResponse =
  /** status 201 Group has been created successfully. */ {
    ok?: boolean;
    data?: GroupResponse;
    message?: string;
  };
export type AddGroupApiArg = {
  /** Group that needs to be added to the application */
  createGroup: CreateGroup;
};
export type UpdateGroupMembersApiResponse =
  /** status 200 The Group's details has been updated successfully */ {
    ok?: boolean;
    data?: GroupResponse;
    message?: string;
  };
export type UpdateGroupMembersApiArg = {
  /** ID of the Group to update */
  id: string;
  /** Group that needs to be added to the application */
  updateGroup: UpdateGroup;
};
export type DeleteGroupApiResponse =
  /** status 200 The Group's details has been deleted successfully */ {
    ok?: boolean;
    data?: string | null;
    message?: string;
  };
export type DeleteGroupApiArg = {
  /** ID of the Group to delete */
  id: string;
  /** flag to hard delete */
  delete?: string;
};
export type GetGroupMembersByIdApiResponse =
  /** status 200 Group Members details found. */ {
    ok?: boolean;
    data?: GroupMembers;
    message?: string;
  };
export type GetGroupMembersByIdApiArg = {
  /** ID of the Group Members to retrieve */
  id: string;
};
export type GetGroupPeopleApiResponse = /** status 200 Group details found. */ {
  ok?: boolean;
  data?: GroupPeople;
  message?: string;
};
export type GetGroupPeopleApiArg = {
  /** ID of the Group to retrieve */
  id?: string;
  /** Search term for filtering Group People */
  search?: string;
};
export type DeleteGroupAssetApiResponse =
  /** status 200 Group asset removed successfully */ {
    ok?: boolean;
    data?: string | null;
    message?: string;
  };
export type DeleteGroupAssetApiArg = {
  /** Group that needs to be added to the application */
  groupDelete: GroupDelete;
};
export type RemoveGroupMemberApiResponse =
  /** status 200 Group member removed successfully */ {
    ok?: boolean;
    data?: string | null;
    message?: string;
  };
export type RemoveGroupMemberApiArg = {
  /** ID of the Group */
  id: string;
  /** People ID of the member to remove */
  peopleId: string;
};
export type MergeGroupAssetsApiResponse =
  /** status 200 Selected group assets processed successfully. */ {
    ok?: boolean;
    data?: MergeAssetResponse;
    message?: string;
  };
export type MergeGroupAssetsApiArg = {
  /** Provide selected groupIds and operation mode */
  body: {
    type?: string;
    properties?: {
      groupIds?: {
        type?: string;
        items?: {
          type?: string;
        };
        example?: string[];
      };
      isReject?: {
        type?: string;
        example?: boolean;
      };
    };
  };
};
export type AddGroupAssetsApiResponse =
  /** status 201 Assets added to the group successfully. */ {
    ok?: boolean;
    data?: string | null;
    message?: string;
  };
export type AddGroupAssetsApiArg = {
  /** Group that needs to be added to the application */
  groupAssets: GroupAssets;
};
export type GetGroupPermissionsApiResponse = /** status 200 Groups found. */ {
  ok?: boolean;
  data?: GroupPermissions;
  message?: string;
};
export type GetGroupPermissionsApiArg = {
  /** Group ID */
  groupId: string;
};
export type UpdateGroupNameApiResponse =
  /** status 200 The Group's details has been updated successfully */ {
    ok?: boolean;
    data?: GroupResponse;
    message?: string;
  };
export type UpdateGroupNameApiArg = {
  /** ID of the Group to update */
  id: string;
  /** Group that needs to be added to the application */
  updateGroupName: UpdateGroupName;
};
export type Group = {
  id: string;
  name: string;
  createdBy: string;
  memberId: string;
  createdAt: string;
  updatedAt: string;
}[];
export type GroupResponse = {
  success?: boolean;
};
export type CreateGroup = {
  name: string;
  members: string[];
  roleId: string;
};
export type UpdateGroup = {
  id: string;
  status: string;
  createdBy: string;
  createdAt: string;
  updatedAt: string;
};
export type GroupMembers = {
  members?: {
    peopleId?: string;
    name?: string;
    profileImage?: string;
  }[];
  memberId?: string;
  isAddPermission?: boolean;
  roleName?: string;
  createdBy?: string;
  subscriberId?: string;
};
export type GroupPeople = {
  id?: string;
  name?: string;
  profileImage?: string;
}[];
export type GroupDelete = {
  userId?: string;
  groupId?: string;
  assetId?: string[];
  memberId?: string;
};
export type MergeAssetResponse = {
  success?: boolean;
};
export type GroupAssets = {
  module: string;
  ids: string;
  userId?: string;
  size: string;
  assetIds: string[];
  subscriberId?: string;
};
export type GroupPermissions = string[];
export type UpdateGroupName = {
  name?: string;
};
export const {
  useGetGroupQuery,
  useAddGroupMutation,
  useUpdateGroupMembersMutation,
  useDeleteGroupMutation,
  useGetGroupMembersByIdQuery,
  useGetGroupPeopleQuery,
  useDeleteGroupAssetMutation,
  useRemoveGroupMemberMutation,
  useMergeGroupAssetsMutation,
  useAddGroupAssetsMutation,
  useGetGroupPermissionsQuery,
  useUpdateGroupNameMutation
} = injectedRtkApi;
